import { supabase } from '../lib/supabase';
import { logger, generateRequestId } from '../utils/logger';

/**
 * Service for user engagement tracking and analytics
 */

export const engagementService = {
  /**
   * Track user engagement event
   */
  async trackEngagement(eventType) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;

      const today = new Date()?.toISOString()?.split('T')?.[0];

      // Get or create today's metrics
      const { data: existing } = await supabase
        ?.from('user_engagement_metrics')?.select('*')?.eq('user_id', user?.id)?.eq('metric_date', today)
        ?.single();

      const updates = {
        user_id: user?.id,
        metric_date: today
      };

      switch (eventType) {
        case 'daily_checkin':
          updates.daily_checkins_count = (existing?.daily_checkins_count || 0) + 1;
          break;
        case 'symptom_entry':
          updates.symptom_entries_count = (existing?.symptom_entries_count || 0) + 1;
          break;
        case 'analytics_view':
          updates.analytics_views_count = (existing?.analytics_views_count || 0) + 1;
          break;
        case 'export':
          updates.exports_count = (existing?.exports_count || 0) + 1;
          break;
      }

      if (existing) {
        await supabase
          ?.from('user_engagement_metrics')
          ?.update(updates)
          ?.eq('id', existing?.id);
      } else {
        await supabase
          ?.from('user_engagement_metrics')
          ?.insert([updates]);
      }

      logger?.debug('Engagement tracked', { eventType, userId: user?.id });
    } catch (error) {
      logger?.error('Engagement tracking error', error, { requestId });
    }
  },

  /**
   * Get engagement metrics for date range
   */
  async getEngagementMetrics(startDate, endDate) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      const { data, error } = await supabase
        ?.from('user_engagement_metrics')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.gte('metric_date', startDate)
        ?.lte('metric_date', endDate)
        ?.order('metric_date', { ascending: true });

      if (error) {
        logger?.error('Failed to fetch engagement metrics', error, { requestId });
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      logger?.error('Engagement metrics fetch error', error, { requestId });
      return { data: null, error: { message: 'Failed to fetch engagement metrics' } };
    }
  },

  /**
   * Calculate engagement score
   */
  async calculateEngagementScore() {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      // Get last 30 days of metrics
      const endDate = new Date()?.toISOString()?.split('T')?.[0];
      const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];

      const { data: metrics } = await this.getEngagementMetrics(startDate, endDate);

      if (!metrics || metrics?.length === 0) {
        return { data: { score: 0, level: 'inactive' }, error: null };
      }

      // Calculate weighted score
      const totalScore = metrics?.reduce((sum, metric) => {
        return sum + 
          (metric?.daily_checkins_count * 10) +
          (metric?.symptom_entries_count * 5) +
          (metric?.analytics_views_count * 2) +
          (metric?.exports_count * 15);
      }, 0);

      const avgScore = totalScore / metrics?.length;
      const normalizedScore = Math.min(avgScore, 100);

      let level = 'low';
      if (normalizedScore >= 70) level = 'high';
      else if (normalizedScore >= 40) level = 'medium';

      return { data: { score: normalizedScore, level, daysTracked: metrics?.length }, error: null };
    } catch (error) {
      logger?.error('Engagement score calculation error', error, { requestId });
      return { data: null, error: { message: 'Failed to calculate engagement score' } };
    }
  },

  /**
   * Get retention insights
   */
  async getRetentionInsights() {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('created_at')
        ?.eq('id', user?.id)
        ?.single();

      const accountAge = Math.floor((Date.now() - new Date(profile?.created_at)?.getTime()) / (1000 * 60 * 60 * 24));

      const { data: engagementScore } = await this.calculateEngagementScore();

      const insights = {
        accountAgeDays: accountAge,
        engagementScore: engagementScore?.score || 0,
        engagementLevel: engagementScore?.level || 'inactive',
        riskLevel: engagementScore?.score < 20 ? 'high' : engagementScore?.score < 50 ? 'medium' : 'low',
        recommendations: []
      };

      if (insights?.engagementScore < 20) {
        insights?.recommendations?.push('Consider setting up daily reminders');
        insights?.recommendations?.push('Explore the analytics dashboard');
      } else if (insights?.engagementScore < 50) {
        insights?.recommendations?.push('Try tracking additional symptoms');
        insights?.recommendations?.push('Connect with a caregiver for support');
      }

      return { data: insights, error: null };
    } catch (error) {
      logger?.error('Retention insights error', error, { requestId });
      return { data: null, error: { message: 'Failed to get retention insights' } };
    }
  }
};

export default engagementService;